import React, { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PromoImage } from "../models/promo-image";

interface CarouselProps {
  slides: PromoImage[];
}

export const Carousel: React.FC<CarouselProps> = ({ slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [startX, setStartX] = useState<number | null>(null);
  const [isTouching, setIsTouching] = useState(false);


  // Auto-scroll effect - fixed timing and dependencies
  useEffect(() => {
    if (slides.length <= 1) return;

    const timer = setInterval(() => {
      if (!isHovering && !isTouching) {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }
    }, 3000); // Changed to 3 seconds

    return () => clearInterval(timer);
  }, [isHovering, isTouching, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setIsTouching(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!startX) return;
    const currentX = e.touches[0].clientX;
    const diffX = startX - currentX;

    // Adjust sensitivity threshold
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
      setStartX(null);
      setIsTouching(false);
    }
  };

  const handleTouchEnd = () => {
    setStartX(null);
    setIsTouching(false);
  };



  return (
    <div
      className="relative w-full h-[250px] md:h-[400px] lg:h-[650px] overflow-hidden"
      style={{ aspectRatio: "16/9" }} // 16:9 ratio for mobile
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Carousel content */}
      <div
        className="flex transition-transform duration-500 ease-out h-full"
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >
        {slides.map((slide) => (
          <div key={slide.id} className="min-w-full h-full relative">
            <img
              src={slide.imgUrl}
              alt="Promo"
              loading="lazy"
              className="w-full h-full object-cover"
          
            />
          </div>
        ))}
      </div>

      {/* Navigation Controls - Hidden on mobile */}
      <div className="absolute inset-0 items-center justify-between px-8 opacity-0 hover:opacity-100 transition-opacity duration-300 hidden md:flex">
        <button
          onClick={prevSlide}
          className="group relative w-12 h-12 flex items-center justify-center"
        >
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-full transform transition-all duration-300 group-hover:scale-110"></div>
          <div className="absolute inset-0 bg-white/5 rounded-full transform transition-all duration-300 group-hover:scale-105"></div>
          <ArrowLeft className="w-5 h-5 text-white transform transition-transform duration-300 group-hover:-translate-x-1" />
        </button>

        <button
          onClick={nextSlide}
          className="group relative w-12 h-12 flex items-center justify-center"
        >
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-full transform transition-all duration-300 group-hover:scale-110"></div>
          <div className="absolute inset-0 bg-white/5 rounded-full transform transition-all duration-300 group-hover:scale-105"></div>
          <ArrowRight className="w-5 h-5 text-white transform transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>

      {/* Carousel Indicators - Responsive design */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 md:gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className="group relative transition-all duration-300"
          >
            {/* Mobile: minimal dots */}
            <div
              className={`md:hidden w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
                index === currentSlide
                  ? "bg-white"
                  : "bg-white/50 group-hover:bg-white/75"
              }`}
            ></div>

            {/* Desktop: elongated indicators */}
            <div
              className={`hidden md:block h-2 rounded-full transition-all duration-300 ${
                index === currentSlide ? "w-8" : "w-2"
              }`}
            >
              <div
                className={`absolute inset-0 rounded-full transition-colors duration-300 ${
                  index === currentSlide
                    ? "bg-white"
                    : "bg-white/50 group-hover:bg-white/75"
                }`}
              ></div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
